// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    Action,
    Translate,
} from '@plesk/ui-library';
import { Button } from 'admin/common/components/Button/Button';
import { getActionColumnProps } from 'common/helpers/list';
import * as React from 'react';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as historyActions from 'common/modules/disasterRecovery/actions';
import List from 'common/components/List/List';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { formatTableDate } from 'common/date';
import { DisasterRecoveryProcessStatus } from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryProcessStatus';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { connect } from 'react-redux';
import {
    DisasterRecoveryStatus,
    IDisasterRecoveryResponse,
    IDisasterRecoveryEntityResponse,
} from 'common/api/resources/DisasterRecovery';
import { DISASTER_RECOVERY_HISTORY } from 'admin/disasterRecovery/constants/test';
import { Loader } from 'common/components';
import DisasterRecoveryHistoryOperations from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryHistoryOperations';
import { Dialog } from 'common/components/Dialog/Dialog';
import OriginalData from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryOriginalData';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { IListRow } from 'common/list';
import DisasterRecoveryProcessRow from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryProcessRow';
import { dataCySelector } from 'common/tests/selectors';
import DisasterRecoveryEntityStatusDialog from 'admin/disasterRecovery/components/HistoryTable/DisasterRecoveryEntityStatusDialog';

const columns = [{
    key: 'colId',
    width: '1%',
    title: <Translate content="disasterRecovery.history.list.id" />,
}, {
    key: 'colComputeResource',
    title: <Translate content="disasterRecovery.history.list.computeResource" />,
}, {
    key: 'colStatus',
    title: <Translate content="disasterRecovery.history.list.status" />,
}, {
    key: 'colStartedAt',
    title: <Translate content="disasterRecovery.history.list.startedAt" />,
}, {
    key: 'colFinishedAt',
    title: <Translate content="disasterRecovery.history.list.finishedAt" />,
}, {
    key: 'colFillerColumn',
    title: '',
    width: '25%',
},
getActionColumnProps({ width: '5%' }),
];

interface IDisasterRecoveryHistoryTableProps extends RouteComponentProps {
    emptyView: React.ReactElement;
}

export type DisasterRecoveryHistoryTableProps =
    IDisasterRecoveryHistoryTableProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const DisasterRecoveryHistoryTable: React.FC<DisasterRecoveryHistoryTableProps> = ({
    processes,
    emptyView,
    isLoadingHistory,
    disasterRecoveryActions: {
        getDisasterRecoveryHistory,
        deleteOriginalData,
    },
}) => {
    const [selection, setSelection] = React.useState<string[]>([]);
    const [entitySelection, setEntitySelection] = React.useState<IDisasterRecoveryEntityResponse[]>([]);
    const [isOriginalDataDialogOpen, setOriginalDataDialogOpen] = React.useState<boolean>(false);
    const [expandedRows, setExpandedRows] = React.useState<string[]>([]);
    const [taskModalOpen, setTaskModalOpen] = React.useState(false);
    const [selectedEntity, setSelectedEntity] = React.useState<IDisasterRecoveryEntityResponse>();

    React.useEffect(() => {
        getDisasterRecoveryHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExpandedRowsChange = (rows: string[]) => setExpandedRows(rows);

    const loadPaginated = (page: number) => getDisasterRecoveryHistory({ page });

    const selectedProcesses = React.useMemo(() =>
        processes.data.filter(process => selection.includes(process.id.toString())),
    [processes, selection]
    );

    const list = processes.data.map((process: IDisasterRecoveryResponse) => {
        const actionEl = (
            <Button
                to={pathTo(`disaster_recovery/compute_resources/${process.source_compute_resource.id}/recovery`)}
                ghost={false}
                intent={INTENT_TYPE.PRIMARY}
                className="action-icon"
                data-cy={dataCySelector(process.id, DISASTER_RECOVERY_HISTORY.RETRY_FAILED_BUTTON)}
                disabled={process.status !== DisasterRecoveryStatus.FAILED}
                tooltip={<Translate content="task.tooltip.retry" />}
            >
                <Translate content="disasterRecovery.history.list.retryButton" />
            </Button>
        );

        return {
            colId: process.id,
            colComputeResource: (
                <Action
                    component={Link}
                    to={pathTo(`compute_resources/${process.source_compute_resource.id}`)}
                >
                    {process.source_compute_resource.name}
                </Action>
            ),
            colStatus: (
                <DisasterRecoveryProcessStatus
                    status={process.status}
                    compact={true}
                    completedProcesses={process.entities_count.running_entities_count}
                    totalProcesses={process.entities_count.total}
                />
            ),
            colStartedAt: process.started_at ? formatTableDate(process.started_at) : null,
            colFinishedAt: process.finished_at ? formatTableDate(process.finished_at) : null,
            colActions: actionEl,
            key: process.id.toString(),
        };
    });

    const handleViewDeleteOriginalData = () => {
        setOriginalDataDialogOpen(true);
    };

    const handleOriginalDataDialogClose = () => {
        setOriginalDataDialogOpen(false);
    };

    const addEntitiesToSelection = (process: IDisasterRecoveryResponse, entities: IDisasterRecoveryEntityResponse[]) => {
        const newEntitySelection = [...entities, ...entitySelection];
        setEntitySelection(newEntitySelection);
        const entityIds = newEntitySelection.map((entity: IDisasterRecoveryEntityResponse) => entity.id);
        // if all entities of the process are selected, add the process to selection
        if ((process.entities?.data ?? []).every((entity) => entityIds.includes(entity.id))) {
            if (!selection.includes(process.id.toString())) {
                setSelection([...selection, process.id.toString()]);
            }
        }
    };

    const removeEntitiesFromSelection = (process: IDisasterRecoveryResponse, entities: IDisasterRecoveryEntityResponse[]) => {
        const entityIds = entities.map((entity: IDisasterRecoveryEntityResponse) => entity.id);
        setEntitySelection(entitySelection.filter(entity => !entityIds.includes(entity.id)));
        setSelection(selection.filter((id: string) => id !== process.id.toString()));
    };

    const handleDeletedManually = async (entityIds: number[]) => {
        await deleteOriginalData({
            disaster_recovery_ids: selection.map(id => parseInt(id, 10)),
            disaster_recovery_entity_ids: entityIds,
            is_deleted_manually: true,
        });

        setOriginalDataDialogOpen(false);
    };

    const handleDeleteOriginalData = async () => {
        deleteOriginalData({
            disaster_recovery_ids: selection.map(id => parseInt(id, 10)),
            disaster_recovery_entity_ids: entitySelection.length > 0 ? entitySelection.map(entity => entity.id) : [],
            is_deleted_manually: false,
        });
    };

    const handleDeleteOriginalDataForEntity = (entity: IDisasterRecoveryEntityResponse) => {
        deleteOriginalData({
            disaster_recovery_entity_ids: [entity.id],
            is_deleted_manually: false,
        });
    };

    const handleTaskModalOpen = (entity: IDisasterRecoveryEntityResponse) => {
        setSelectedEntity(entity);
        setTaskModalOpen(true);
    };

    const renderRow = (row: IListRow) => {
        const process = processes.data.find(item => item.id.toString() === row.key);
        if (process === undefined) {
            return;
        }

        return (
            <DisasterRecoveryProcessRow
                isLoading={!!process.is_loading}
                process={process}
                isProcessSelected={selection.includes(process.id.toString())}
                handleTaskModalOpen={handleTaskModalOpen}
                handleAddEntitiesToSelection={addEntitiesToSelection}
                handleRemoveEntitiesFromSelection={removeEntitiesFromSelection}
                handleDeleteOriginalData={handleDeleteOriginalDataForEntity}
            />
        );
    };

    const handleSelection = (ids: string[]) => {
        const deselectedIds: string[] = selection.filter(id => !ids.includes(id));
        const deselectedEntityIds: number[] = [];
        processes.data.filter(process => deselectedIds.includes(process.id.toString()))
            .forEach(process => {
                process.entities?.data.forEach(el => deselectedEntityIds.push(el.id));
            });

        setSelection(ids);
        setEntitySelection(entitySelection.filter(el => !deselectedEntityIds.includes(el.id)));
    };

    const handleTaskDialogClose = () => setTaskModalOpen(false);

    return (
        <Loader isLoading={isLoadingHistory}>
            <List
                isLoading={isLoadingHistory}
                loadItems={loadPaginated}
                columns={columns}
                data={list}
                meta={processes.meta}
                emptyView={emptyView}
                selection={selection}
                onSelectionChange={handleSelection}
                onExpandedRowsChange={handleExpandedRowsChange}
                expandedRows={expandedRows}
                renderRowBody={renderRow}
                toolbar={(
                    <DisasterRecoveryHistoryOperations
                        handleViewDeleteOriginalData={handleViewDeleteOriginalData}
                        handleDeleteOriginalData={handleDeleteOriginalData}
                        entities={entitySelection}
                        processes={selectedProcesses}
                    />
                )}
            />
            <Dialog
                heading={<Translate content="disasterRecovery.history.originalDataDialog.title"/>}
                closeHandler={handleOriginalDataDialogClose}
                isOpen={isOriginalDataDialogOpen}
                size={SIZE.MD}
            >
                <OriginalData
                    entityIds={entitySelection.map((entity) => entity.id)}
                    processIds={selection.map(id => parseInt(id, 10))}
                    handleDeletedManually={handleDeletedManually}
                />
            </Dialog>
            {selectedEntity && <DisasterRecoveryEntityStatusDialog
                handleClose={handleTaskDialogClose}
                isOpen={taskModalOpen}
                entity={selectedEntity}
            />
            }
        </Loader>
    );
};

const mapStateToProps = (state: RootState) => ({
    processes: state.disasterRecoveryHistory.list,
    isLoadingHistory: state.app.loadingFlags.has(LOADING_FLAGS.DISASTER_RECOVERY_HISTORY),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    disasterRecoveryActions: bindActionCreators(historyActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DisasterRecoveryHistoryTable));
